import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import AppConst from '../apis/appconst';   // <-- FIX THE RELATIVE PATH to match this file's location

export const THEME = {
  // ---- colors ----
  headerBg:  [180, 219, 250],
  headerTxt: [0, 0, 0],
  border:    [0, 0, 0],


  flags: {
    high:       { label: 'H', color: [0, 0, 0]   },  // red
    low:        { label: 'L', color: [0, 0, 0]   },  // blue
    borderline: { label: 'B', color: [0, 0, 0]  },  // orange
    bold:       1,          // 1 = flagged results also bold
    boldColor:  [0, 0, 0]   // color for the manual is_bold checkbox
  },

  // ---- outlines: 1 = draw, 0 = hide ----
  outline: {
    patientBox:   1,   // patient details box
    testTable:    0,   // box around each test's result table  for test outline 
    deptHeader:   1,   // department band border
    interpBox:    1,   // Interpretation / Impression / Notes box
    radius:       0   // corner rounding (0 = square)
  },

  // ---- barcode: 1 = show, 0 = hide ----
  
  // ---- font sizes ----
  font: {
    deptHeader:   12,  // department band
    tableHead:    9,   // TEST NAME / RESULT / ...
    tableBody:    9,   // param rows
    testName:     9,   // main test name row
    sectionTitle: 9,   // "Interpretation" / "Impression" / "Notes:"
    editorTitle:  10,  // Editor-type param heading
    patient:      10,  // patient details box
    barcodeText:  7,
    endOfReport:  12,
    pageNumber:   8
  },

 qr: {
  show:    1,
  size:    17,     // pt (square)
  gapTop:  -3,      // + moves QR down, − moves it up (from centered)
  gapRight: 3,     // distance from the box's right edge
  scale:   4,
  margin:  0,
  ecLevel: 'M'
},

  barcode: { show: 0, width: 30, height: 6, gapTop: 1.3, showText: 1 },


genderRange: {
  enabled:         1,   // 1 = auto-evaluate M/F ranges for Text params
  showOnlyMatched: 0    // 1 = print only the patient's range in the PDF column
},
  // ---- column alignment + widths (must total ~191) ----
  columns: {
    testName: { width: 50, halign: 'left'   },
    result:   { width: 36, halign: 'left' },
    unit:     { width: 22, halign: 'left' },
    range:    { width: 43, halign: 'left' },
    method:   { width: 40, halign: 'left' }
  }
};

/* =====================================================================
   LOAD THEME FROM BACKEND
   THEME above stays as the DEFAULT / fallback. This fetches the saved
   Report Configuration and overwrites THEME in place. If the request
   fails, the defaults above are used, so the report never breaks.

   >>> YOU MUST await this ONCE before building the PDF <<<
       await loadThemeConfig();   // then prepareCodes(), then draw
   ===================================================================== */

// Use the SAME base + route your app uses everywhere else.
// (Add getReportConfigUrl to appconst — e.g. "get_report_config".)
const CONFIG_URL = AppConst.BASE_URL + AppConst.getReportConfigUrl;

// deep-merge fetched values into the existing THEME object (mutates in place).
// arrays like [r,g,b] are replaced whole; nested objects merge key-by-key.
const _mergeInto = (target, src) => {
  for (const k of Object.keys(src || {})) {
    const sv = src[k], tv = target[k];
    if (sv && typeof sv === 'object' && !Array.isArray(sv) &&
        tv && typeof tv === 'object' && !Array.isArray(tv)) {
      _mergeInto(tv, sv);
    } else {
      target[k] = sv;
    }
  }
  return target;
};

let _themeLoaded = false;
export const loadThemeConfig = async (key = 'default') => {
  if (_themeLoaded) return THEME;                 // fetch only once per page load
  try {
    const url = `${CONFIG_URL}?key=${encodeURIComponent(key)}`;
    console.debug('[theme] fetching config from:', url);   // <-- check this in DevTools
    const res  = await fetch(url, { headers: { Accept: 'application/json' } });
    const json = await res.json();
    console.debug('[theme] config response:', json);       // <-- must contain { theme: {...} }
    if (json && json.theme) {
      _mergeInto(THEME, json.theme);
      _themeLoaded = true;
    } else {
      console.warn('[theme] response had no "theme" key — using defaults.', json);
    }
  } catch (e) {
    console.warn('[theme] config load failed — using defaults.', e);
  }
  return THEME;
};

// Optional: call this after SAVING new config so the next PDF re-fetches.
export const resetThemeCache = () => { _themeLoaded = false; };

/* ---------- shared helpers ---------- */

export const drawOutline = (doc, enabled, x, y, w, h, style = 'S') => {
  if (!enabled) return;
  const X = +x, Y = +y, W = +w, H = +h;
  if (![X, Y, W, H].every(Number.isFinite) || W <= 0 || H <= 0) return;
  doc.setDrawColor(...THEME.border);
  doc.setLineWidth(0.2);
  const r = Number(THEME.outline.radius) || 0;
  if (r > 0) doc.roundedRect(X, Y, W, H, r, r, style);
  else doc.rect(X, Y, W, H, style);
};

export const evaluateFlag = (resultRaw, min, max) => {
  const v = parseFloat(resultRaw);
  if (isNaN(v) || isNaN(min) || isNaN(max)) return null;
  const lo = Math.min(min, max), hi = Math.max(min, max), EPS = 1e-9;
  if (Math.abs(v - hi) < EPS || Math.abs(v - lo) < EPS) return THEME.flags.borderline;
  if (v > hi) return THEME.flags.high;
  if (v < lo) return THEME.flags.low;
  return null;
};

export const getAgeDisplay = (p) => {
  if (!p) return 'N/A';
  const y = +(p.lab_reg_age_year || 0), m = +(p.lab_reg_age_month || 0), d = +(p.lab_reg_age_day || 0);
  if (!y && !m && !d) return 'N/A';
  const parts = [];
  if (y > 0) parts.push(`${y}Y${y > 1 ? 's' : ''}`);
  if (m > 0) parts.push(`${m}M${m > 1 ? 's' : ''}`);
  if (d > 0) parts.push(`${d}D${d > 1 ? 's' : ''}`);
  return parts.join(' ');
};
// Call ONCE before building the PDF. Returns plain dataURLs.
export const prepareCodes = async (patient) => {
  const [qr, barcode] = await Promise.all([
    THEME.qr.show ? makeQr(patient) : Promise.resolve(null),
    Promise.resolve(THEME.barcode.show ? makeBarcode(patient?.lab_reg_uhid) : null)
  ]);
  return { qr, barcode };
};

/* ---------- QR: SHORT payload, scannable ---------- */
export const buildQrText = (patient = {}) => {
  const title = patient.lab_reg_name_title ? String(patient.lab_reg_name_title).trim() + ' ' : '';
  const lines = [
    `UHID:${patient.lab_reg_uhid || ''}`,
    `PID:${patient.lab_reg_id || ''}`,
    `Name:${title}${patient.lab_reg_name || ''}`,
    `Age:${getAgeDisplay(patient)}/${(patient.lab_reg_gender || '').charAt(0)}`
  ];
  if (patient.lab_reg_sample_datetime) {
    lines.push(`Sample:${String(patient.lab_reg_sample_datetime).split(' ')[0]}`);
  }
  return lines.join('\n');
};

const _qrCache = {};
export const makeQr = async (patient) => {
  if (!THEME.qr.show) return null;
  const text = buildQrText(patient);
  if (_qrCache[text]) return _qrCache[text];
  try {
    const url = await QRCode.toDataURL(text, {
      errorCorrectionLevel: THEME.qr.ecLevel,
      margin: THEME.qr.margin,
      scale: THEME.qr.scale
    });
    _qrCache[text] = url;
    return url;
  } catch (e) {
    console.warn('QR generation failed', e);
    return null;
  }
};

const _barcodeCache = {};


/* ---------- gender-aware ranges ---------- */

export const normalizeGender = (g) => {
  const s = String(g || '').trim().toLowerCase();
  if (s.startsWith('m')) return 'M';
  if (s.startsWith('f')) return 'F';
  return null;
};

export const parseGenderRanges = (rangeRaw) => {
  const s = String(rangeRaw || '').replace(/[\u2013\u2014]/g, '-').trim();
  if (!s) return null;

  const re = /\b(m|male|f|female)\b\s*[:=-]?\s*([\d.]+)\s*(?:-|to)\s*([\d.]+)/gi;
  const out = {};
  let m;
  while ((m = re.exec(s)) !== null) {
    const key = normalizeGender(m[1]);
    const lo = parseFloat(m[2]), hi = parseFloat(m[3]);
    if (!key || isNaN(lo) || isNaN(hi)) continue;
    out[key] = { min: Math.min(lo, hi), max: Math.max(lo, hi) };
  }
  return Object.keys(out).length ? out : null;
};

export const evaluateFlagSmart = (resultRaw, rangeText, patient) => {
  if (isNaN(parseFloat(resultRaw))) return null;
  const s = String(rangeText || '').replace(/[\u2013\u2014]/g, '-').trim();

  const simple = s.match(/^\s*([\d.]+)\s*-\s*([\d.]+)\s*$/);
  if (simple) return evaluateFlag(resultRaw, parseFloat(simple[1]), parseFloat(simple[2]));

  if (!THEME.genderRange.enabled) return null;
  const g = normalizeGender(patient?.lab_reg_gender);
  if (!g) return null;

  const byG = parseGenderRanges(s);
  if (!byG || !byG[g]) return null;
  return evaluateFlag(resultRaw, byG[g].min, byG[g].max);
};

export const genderRangeDisplay = (rangeText, patient) => {
  if (!THEME.genderRange.showOnlyMatched) return rangeText;
  const g = normalizeGender(patient?.lab_reg_gender);
  const byG = parseGenderRanges(rangeText);
  if (!g || !byG || !byG[g]) return rangeText;
  return `${byG[g].min} - ${byG[g].max}`;
};


export const makeBarcode = (uhid) => {
  const val = String(uhid || '').trim();
  if (!val) return null;
  if (_barcodeCache[val]) return _barcodeCache[val];
  try {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, val, {
      format: 'CODE128', displayValue: false, margin: 0, width: 2, height: 60
    });
    const url = canvas.toDataURL('image/png');
    _barcodeCache[val] = url;
    return url;
  } catch (e) {
    console.warn('barcode generation failed', e);
    return null;
  }
};